const rawApiBaseUrl = import.meta.env.VITE_API_URL?.trim();

export const API_BASE_URL = rawApiBaseUrl ? rawApiBaseUrl.replace(/\/$/, '') : '/api';

export function buildApiUrl(path = '') {
  if (!path) {
    return API_BASE_URL;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE_URL || API_BASE_URL === '.') {
    return normalizedPath;
  }

  return `${API_BASE_URL}${normalizedPath}`;
}

export async function readResponseData(response) {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) {
    return null;
  }

  if (!contentType.includes('application/json')) {
    return text;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(data, fallback) {
  if (!data) {
    return fallback;
  }

  if (typeof data === 'string') {
    return data;
  }

  if (typeof data !== 'object') {
    return fallback;
  }

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (data.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors)
      .flat()
      .find((value) => typeof value === 'string' && value.trim());

    if (firstError) {
      return firstError;
    }
  }

  return fallback;
}

export function getErrorMessage(data, fallback) {
  return extractErrorMessage(data, fallback);
}

export async function fetchJson(path, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
  };

  if (options.body != null && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers,
  });
  const data = await readResponseData(response);

  if (!response.ok) {
    const error = new Error(extractErrorMessage(data, `Request failed with status ${response.status}`));
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}

export async function fetchHealth(path = '/health', options = {}) {
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });
  const data = await readResponseData(response);

  return {
    ok: response.ok,
    status: response.status,
    data,
  };
}
